import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import IQualifyLogo from "../components/IQualifyLogo";


export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  if (user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Vui lòng nhập email và mật khẩu");
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Đăng nhập thành công!");
      navigate("/", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Đăng nhập thất bại");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden" style={{ fontFamily: "'Outfit', 'Inter', sans-serif" }}>

      {/* ─── LEFT PANEL: Branding ─── */}
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center"
           style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #14b8a6 100%)" }}>

        {/* Decorative floating circles */}
        <div className="absolute top-20 left-16 w-72 h-72 bg-white/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-400/10 rounded-full blur-3xl" />
        <div className="absolute top-1/3 right-1/4 w-40 h-40 border border-white/10 rounded-full" />
        <div className="absolute bottom-1/3 left-1/4 w-24 h-24 border border-white/10 rounded-full" />

        {/* Zen wave floating pattern (decorative) */}
        <svg className="absolute inset-0 w-full h-full opacity-5" viewBox="0 0 800 600">
          <path d="M 0 300 Q 200 100 400 300 T 800 300" fill="none" stroke="white" strokeWidth="2" />
          <path d="M 0 350 Q 200 150 400 350 T 800 350" fill="none" stroke="white" strokeWidth="1.5" />
          <path d="M 0 400 Q 200 200 400 400 T 800 400" fill="none" stroke="white" strokeWidth="1" />
        </svg>

        <div className="relative z-10 text-center px-12">
          {/* Large logo */}
          <div className="flex justify-center mb-8">
            <div className="w-36 h-36 p-4 bg-white/10 backdrop-blur-md rounded-3xl border border-white/20 shadow-2xl">
              <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className="w-full h-full drop-shadow-lg">
                <defs>
                  <linearGradient id="login-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#93c5fd" />
                    <stop offset="100%" stopColor="#5eead4" />
                  </linearGradient>
                </defs>
                <path d="M 50 10 A 40 40 0 1 0 85 70 L 90 90 L 70 85 A 40 40 0 0 0 50 10 Z" fill="none" stroke="url(#login-grad)" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M 35 30 Q 60 40 40 60 T 65 75" fill="none" stroke="url(#login-grad)" strokeWidth="5" strokeLinecap="round" />
              </svg>
            </div>
          </div>

          <h1 className="text-5xl font-bold text-white tracking-tight mb-3">
            <span className="font-light text-blue-200">I</span>
            <span className="text-teal-300">-</span>
            Qualify
          </h1>
          <p className="text-lg text-blue-200/80 max-w-sm mx-auto leading-relaxed">
            AI-Powered Conversation Quality Evaluator
          </p>

          {/* Tagline */}
          <div className="mt-10 flex items-center justify-center gap-6">
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-2">
                <svg className="w-5 h-5 text-teal-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-xs text-blue-200/70">Đánh giá</span>
            </div>
            <div className="w-px h-8 bg-white/20" />
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-2">
                <svg className="w-5 h-5 text-teal-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <span className="text-xs text-blue-200/70">Hội thoại</span>
            </div>
            <div className="w-px h-8 bg-white/20" />
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center mb-2">
                <svg className="w-5 h-5 text-teal-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <span className="text-xs text-blue-200/70">Thấu cảm</span>
            </div>
          </div>
        </div>
      </div>

      {/* ─── RIGHT PANEL: Login Form ─── */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-gray-50 p-4 sm:p-6 min-h-screen overflow-y-auto">
        <div className="w-full max-w-md my-auto py-4 sm:py-6">

          {/* Mobile logo (hidden on desktop) */}
          <div className="lg:hidden text-center mb-8">
            <IQualifyLogo size="lg" className="justify-center mb-3" />
            <p className="text-sm text-gray-500">AI-Powered Conversation Quality Evaluator</p>
          </div>

          {/* Welcome text */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Chào mừng trở lại 👋</h2>
            <p className="text-gray-500 mt-1">Đăng nhập để tiếp tục vào hệ thống đánh giá hội thoại</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-white"
                  placeholder="admin@iqualify.com"
                  autoComplete="email"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Mật khẩu</label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-white"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 text-base font-semibold text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: "linear-gradient(135deg, #1e3a8a, #14b8a6)" }}
            >
              {loading ? (
                <span className="flex items-center justify-center space-x-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>Đang đăng nhập...</span>
                </span>
              ) : (
                "Đăng nhập"
              )}
            </button>

          </form>

          {/* Footer */}
          <p className="text-center text-xs text-gray-400 mt-8">
            © 2026 i-Qualify · AI Conversation Quality Evaluator
          </p>
        </div>
      </div>
    </div>
  );
}
